import { httpClient } from 'providers/clients/httpClient';

// -----------------------------------------------
// ------------ NOTIFICATION TYPES ---------------
// -----------------------------------------------

export type CreateUserNotificationParams = {
  title: string;
  body: string;
  link?: {
    url: string;
    title: string;
  };
};

export interface NotificationMessage {
  id: string;
  title?: string;
  body?: string;
  preview?: {
    url?: string;
    width?: number;
    height?: number;
  } | null;
  link?: {
    url?: string;
    title?: string | null;
  } | null;
  creator?: {
    name?: string;
    url?: string;
    avatar?: string;
  } | null;
  created?: string;
  expires?: string;
  read?: boolean;
  dismissed?: boolean;
  app?: string;
  topic?: NotificationTopic;
  category?: string;
  icon?: string;
}

/** Narrowed type for widget alert notifications (app=insights, topic.name=alert). */
export interface WidgetAlertNotification extends NotificationMessage {
  app: 'insights';
  widget_id?: string;
  dashboard_id?: string;
}

/** Raw API shape for a widget alert notification (app=insights, topic.name=alert). */
export interface WidgetAlertNotificationMessage {
  id: string;
  content: {
    title?: string;
    body?: string;
    link?: {
      url?: string;
      title?: string | null;
    } | null;
    preview?: {
      url?: string;
      width?: number;
      height?: number;
    } | null;
  };
  topic: {
    app: string;
    name: string;
    context?: {
      params?: {
        action?: string;
        widget_id?: string;
        dashboard_id?: string;
        [key: string]: string | undefined;
      };
      client_params?: any[];
      default_return_url?: string;
    };
  };
  creator?: {
    name?: string;
    url?: string;
    avatar?: string;
  } | null;
  created?: string;
  expires?: string;
  targeted?: any | null;
  group_key?: string;
  is_read?: boolean;
}

export interface NotificationsResponse {
  data: NotificationMessage[];
  total: number;
  unread_count: number;
}

export interface NotificationTopicContext {
  params?: Record<string, string>;
  client_params?: unknown[];
  default_return_url?: string;
}

export interface NotificationTopic {
  app: string;
  name: string;
  context?: NotificationTopicContext;
  description?: string;
}

export interface NotificationSubscription {
  app: string;
  topic: string;
  subscribed: boolean;
  drivers?: string[];
}

export interface NotificationMessageContext {
  params: Record<string, string>;
  client_params: unknown[];
  default_return_url: string;
}

export interface NotificationMessageTopic {
  app: string;
  name: string;
  context: NotificationMessageContext;
}

export interface NotificationMessageContent {
  title: string;
  body: string;
  link: { url: string; title: string | null } | null;
  preview: unknown | null;
}

export interface NotificationMessageDetail {
  id: string;
  content: NotificationMessageContent;
  topic: NotificationMessageTopic;
  creator: unknown | null;
  created: string;
  expires: string;
  targeted: unknown | null;
  group_key: string | null;
}

export interface NotificationMessageResponse {
  status: string;
  data: NotificationMessageDetail;
}

export type GetNotificationsParams = {
  app?: string;
  topic?: string;
  unreadOnly?: boolean;
};

export type MarkNotificationsReadParams = {
  message_ids: string[];
};

export type DismissNotificationsParams = {
  message_ids: string[];
};

export type UpdateSubscriptionParams = {
  app: string;
  topic: string;
  subscribed: boolean;
};

/** Type guard — narrows a NotificationMessage to a WidgetAlertNotification. */
export function isWidgetAlertNotification(
  n: NotificationMessage,
): n is WidgetAlertNotification {
  return n.app === 'insights' && n.topic?.name === 'alert';
}

/**
 * Normalizes raw API items into the NotificationMessage shape.
 * Extracted so both getNotifications and consumers have a single
 * source of truth for the transformation logic.
 */
function normalizeNotificationItems(rawItems: any[]): NotificationMessage[] {
  return rawItems.map((item) => {
    const content = item.content || {};
    const topicObj: WidgetAlertNotificationMessage['topic'] | undefined =
      item.topic && typeof item.topic === 'object' ? item.topic : undefined;
    const topicName: string | undefined =
      topicObj?.name ?? (typeof item.topic === 'string' ? item.topic : undefined);
    const appName: string | undefined = topicObj?.app ?? item.app ?? undefined;

    const topicValue: NotificationTopic | undefined = topicObj
      ? {
          app: topicObj.app,
          name: topicObj.name,
          context: topicObj.context as NotificationTopicContext | undefined,
        }
      : topicName
        ? { app: appName ?? '', name: topicName }
        : undefined;

    const base = {
      id: item.id,
      title: item.title || content.title || undefined,
      body: item.body || content.body || undefined,
      link: item.link || content.link || null,
      preview: item.preview || content.preview || null,
      creator: item.creator || null,
      created: item.created,
      expires: item.expires,
      read: typeof item.is_read === 'boolean' ? item.is_read : Boolean(item.read),
      dismissed: item.dismissed || false,
      app: appName,
      topic: topicValue,
      category: item.category || undefined,
      icon: item.icon || undefined,
    };

    if (appName === 'insights' && topicName === 'alert') {
      return {
        ...base,
        widget_id: topicValue?.context?.params?.widget_id || undefined,
        dashboard_id: topicValue?.context?.params?.dashboard_id || undefined,
      } as WidgetAlertNotification;
    }

    return base as NotificationMessage;
  });
}

// -----------------------------------------------
// ------------ NOTIFICATIONS PROVIDER -----------
// -----------------------------------------------
export const notificationsProvider = {
  /**
   * GET /core/notifications/
   * Fetches a list of notifications with optional filters.
   */
  getNotifications: async (
    params?: GetNotificationsParams,
  ): Promise<NotificationsResponse> => {
    try {
      const queryParams: string[] = [];
      if (params?.app) queryParams.push(`app=${encodeURIComponent(params.app)}`);
      if (params?.topic) queryParams.push(`topic=${encodeURIComponent(params.topic)}`);
      if (params?.unreadOnly) queryParams.push('unread=true');

      const url =
        queryParams.length > 0
          ? `/core/notifications/?${queryParams.join('&')}`
          : '/core/notifications/';

      const { data } = await httpClient({ url, method: 'GET' });

      const rawItems: any[] = Array.isArray(data?.data?.items) ? data.data.items : [];
      const notificationsArray = normalizeNotificationItems(rawItems);
      const totalCount = data?.data?.total || notificationsArray.length || 0;

      return {
        data: notificationsArray,
        total: totalCount,
        unread_count: notificationsArray.filter((n) => !n.read).length || 0,
      };
    } catch (error) {
      console.error('[getNotifications] Error:', error);
      throw error;
    }
  },

  /**
   * GET /core/notifications/message/{message_id}/
   * Fetches a single notification message by ID.
   */
  getNotificationMessage: async (
    messageId: string,
  ): Promise<NotificationMessageResponse> => {
    try {
      const { data } = await httpClient({
        url: `/core/notifications/message/${messageId}/`,
        method: 'GET',
      });
      return data as NotificationMessageResponse;
    } catch (error) {
      console.error('[getNotificationMessage] Error:', error);
      throw error;
    }
  },

  /**
   * POST /core/notifications/read/
   * Marks one or more notifications as read.
   */
  markNotificationsRead: async (params: MarkNotificationsReadParams): Promise<void> => {
    try {
      const { data } = await httpClient({
        url: '/core/notifications/read/',
        method: 'POST',
        data: { message_ids: params.message_ids },
      });
      return data;
    } catch (error) {
      console.error('[markNotificationsRead] Error:', error);
      throw error;
    }
  },

  /**
   * POST /core/notifications/dismiss/
   * Dismisses one or more notifications.
   */
  dismissNotifications: async (params: DismissNotificationsParams): Promise<void> => {
    try {
      const { data } = await httpClient({
        url: '/core/notifications/dismiss/',
        method: 'POST',
        data: { message_ids: params.message_ids },
      });
      return data;
    } catch (error) {
      console.error('[dismissNotifications] Error:', error);
      throw error;
    }
  },

  /**
   * GET /core/notifications/topic/
   * Fetches available notification topics.
   */
  getNotificationTopics: async (): Promise<NotificationTopic[]> => {
    try {
      const { data } = await httpClient({
        url: '/core/notifications/topic/',
        method: 'GET',
      });
      return data?.data?.items || [];
    } catch (error) {
      console.error('[getNotificationTopics] Error:', error);
      throw error;
    }
  },

  /**
   * GET /core/notifications/topic/subscription/
   * Fetches the user's topic subscriptions.
   */
  getNotificationSubscriptions: async (): Promise<NotificationSubscription[]> => {
    try {
      const { data } = await httpClient({
        url: '/core/notifications/topic/subscription/',
        method: 'GET',
      });
      return data?.data?.items || [];
    } catch (error) {
      console.error('[getNotificationSubscriptions] Error:', error);
      throw error;
    }
  },

  /**
   * POST or DELETE /core/notifications/topic/subscription/{app}/{topic}/
   * Subscribes or unsubscribes from a notification topic.
   */
  updateNotificationSubscription: async (
    params: UpdateSubscriptionParams,
  ): Promise<void> => {
    try {
      const { data } = await httpClient({
        url: `/core/notifications/topic/subscription/${params.app}/${params.topic}/`,
        method: params.subscribed ? 'POST' : 'DELETE',
      });
      return data;
    } catch (error) {
      console.error('[updateNotificationSubscription] Error:', error);
      throw error;
    }
  },

  /**
   * POST /core/notifications/user/
   * Creates a user notification.
   */
  createUserNotification: async (params: CreateUserNotificationParams): Promise<void> => {
    try {
      const { data } = await httpClient({
        url: '/core/notifications/user/',
        method: 'POST',
        data: JSON.stringify({
          title: params.title,
          body: params.body,
          link: params.link,
        }),
      });
      return data;
    } catch (error) {
      console.error('[createUserNotification] Error:', error);
      throw error;
    }
  },
} as const;

export type NotificationsProviderType = typeof notificationsProvider;
